import { style, globalStyle } from '@vanilla-extract/css'
import { vars, focusRing } from '../design'
import { utility } from '../design/primitives/text.css'

const WIDE = 'screen and (min-width: 820px)'

// The notes section — the station's own writing. It is a READING surface, so it takes the
// Journal's editorial grammar (ruled index, serif prose at the prose measure) rather than
// inventing a third one. No cards: an entry is a ruled row, the way a contents page sets one.
export const section = style({ display: 'flex', flexDirection: 'column', gap: vars.space[5] })

export const head = style({
  paddingBottom: vars.space[3],
  borderBottom: `2px solid ${vars.color.ink}`,
})

// ---- The feed ----------------------------------------------------------
// Notes newest-first, read straight down. Each is a ruled article at the same measure as a note
// opened on its own, so drilling in changes how much you see, never how it is set.
export const feed = style({ display: 'flex', flexDirection: 'column' })

export const post = style({
  maxWidth: vars.measure.wide,
  padding: `${vars.space[5]} 0`,
  borderBottom: `1px solid ${vars.color.rule}`,
  selectors: { '&:first-child': { paddingTop: 0 } },
})

export const postHead = style({ margin: `0 0 ${vars.space[4]}` })

export const postTitle = style({
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs.xl,
  lineHeight: vars.lh.snug,
  letterSpacing: '-0.015em',
  textWrap: 'balance',
  color: vars.color.ink,
  margin: `${vars.space[1]} 0 0`,
})

// The title is the note's permalink. It reads as a heading, and owns up to being a link on hover.
export const postLink = style({
  color: 'inherit',
  textDecoration: 'none',
  selectors: {
    '&:hover': { textDecoration: 'underline', textUnderlineOffset: vars.uline.prose },
    '&:focus-visible': focusRing,
  },
})

export const onward = style({ margin: `${vars.space[2]} 0 0` })
export const onwardLink = style([
  utility,
  {
    color: vars.color.ink,
    textDecoration: 'underline',
    textUnderlineOffset: vars.uline.prose,
    selectors: { '&:focus-visible': focusRing },
  },
])

// The dateline over a feed entry and under a note's title — one treatment, both places, and the
// publication's own label voice rather than a fourth copy of it.
export const stamp = utility

// ---- One note -----------------------------------------------------------
export const piece = style({ maxWidth: vars.measure.wide })

export const title = style({
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs['2xl'],
  lineHeight: 1.04,
  letterSpacing: '-0.022em',
  textWrap: 'balance',
  color: vars.color.ink,
  margin: `0 0 ${vars.space[2]}`,
  '@media': { [WIDE]: { fontSize: vars.fs['3xl'] } },
})

export const pieceHead = style({
  margin: `0 0 ${vars.space[5]}`,
  paddingBottom: vars.space[4],
  borderBottom: `1px solid ${vars.color.rule}`,
})

export const standfirst = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.lg,
  lineHeight: vars.lh.snug,
  textWrap: 'pretty',
  color: vars.color.ink2,
  margin: `${vars.space[2]} 0 0`,
})

// The body — single column at the prose measure. A note is an essay somebody reads through, not
// a front page: no justification, no column rule, no drop cap.
export const body = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.relaxed,
  color: vars.color.ink,
  maxWidth: vars.measure.prose,
})
globalStyle(`${body} p`, { margin: `0 0 ${vars.space[4]}` })
globalStyle(`${body} h2, ${body} h3`, {
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs.lg,
  lineHeight: vars.lh.snug,
  margin: `${vars.space[5]} 0 ${vars.space[2]}`,
})
globalStyle(`${body} ul`, { margin: `0 0 ${vars.space[4]}`, paddingLeft: vars.space[5] })
globalStyle(`${body} li`, { margin: `0 0 ${vars.space[1]}` })
globalStyle(`${body} hr`, {
  border: 0,
  borderTop: `1px solid ${vars.color.rule}`,
  margin: `${vars.space[5]} 0`,
})
globalStyle(`${body} a`, {
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: vars.uline.prose,
})

// What a hosted blog's markup adds over the old markdown desk: pictures with captions, ordered
// lists, quotations and code. Square-cornered and ruled like everything else here; a picture is
// never wider than the prose it sits in.
globalStyle(`${body} ol`, { margin: `0 0 ${vars.space[4]}`, paddingLeft: vars.space[5] })
globalStyle(`${body} img`, { display: 'block', maxWidth: '100%', height: 'auto' })
// A bare picture (Ghost sends the feature image as one) takes a paragraph's spacing; inside a
// figure the figure owns the spacing.
globalStyle(`${body} > img`, { margin: `0 0 ${vars.space[4]}` })
globalStyle(`${body} figure`, { margin: `${vars.space[5]} 0` })
globalStyle(`${body} figcaption`, {
  fontFamily: vars.font.mono,
  fontSize: vars.fs.xs,
  color: vars.color.ink2,
  marginTop: vars.space[2],
})
globalStyle(`${body} blockquote`, {
  margin: `0 0 ${vars.space[4]}`,
  paddingLeft: vars.space[4],
  borderLeft: `2px solid ${vars.color.ink}`,
  color: vars.color.ink2,
})
globalStyle(`${body} pre`, {
  fontFamily: vars.font.mono,
  fontSize: vars.fs.sm,
  lineHeight: vars.lh.normal,
  background: vars.color.paper2,
  padding: vars.space[3],
  margin: `0 0 ${vars.space[4]}`,
  overflowX: 'auto',
})
globalStyle(`${body} code`, { fontFamily: vars.font.mono, fontSize: vars.fs.sm })

// The post's feature image, above the body at the same measure.
export const lead = style({
  display: 'block',
  width: '100%',
  maxWidth: vars.measure.prose,
  height: 'auto',
  margin: `0 0 ${vars.space[5]}`,
})

export const back = style({ margin: `0 0 ${vars.space[4]}` })
